import React, { useState } from "react";
import parse from "html-react-parser";
import { Reference, SkillItem } from "../types";
import { useProfile } from "../context/ProfileContext";

type Props = {
  entry: Reference;
  allSkillItems: SkillItem[];
  isSubEntry?: boolean;
  isLast?: boolean;
};

function getSkillLevel(name: string, items: SkillItem[]): string | null {
  const found = items.find((si) => si.items.indexOf(name) !== -1);
  return found ? found.level : null;
}

function sortByLevel(a: string, b: string, items: SkillItem[]): number {
  const order: Record<string, number> = { REGULAR: 0, SOMETIMES: 1, OLD: 2 };
  const lA = getSkillLevel(a, items);
  const lB = getSkillLevel(b, items);
  const oA = lA ? order[lA] ?? 3 : 3;
  const oB = lB ? order[lB] ?? 3 : 3;
  if (oA !== oB) return oA - oB;
  return a.localeCompare(b);
}

export default function ExperienceEntry({
  entry,
  allSkillItems,
  isSubEntry = false,
  isLast = false,
}: Props) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const { isExperienceRelevant, isSkillRelevant, profile } = useProfile();

  const relevant = isExperienceRelevant(entry.skills);
  const hasSubs = entry.subreferences && entry.subreferences.length > 0;
  const relevantBySub = hasSubs
    ? entry.subreferences!.some((sub) => isExperienceRelevant(sub.skills))
    : relevant;
  const muted = profile !== "all" && !relevant && !relevantBySub;

  const hasDescriptions =
    (entry.descriptions && entry.descriptions.length > 0) ||
    (entry.context && entry.context.length > 0);

  const skills = entry.skills
    ? entry.skills.sort((a, b) => sortByLevel(a, b, allSkillItems))
    : [];

  return (
    <div
      className={`relative flex gap-4 transition-opacity duration-500 print-avoid-break ${
        muted ? "opacity-20" : "opacity-100"
      }`}
    >
      {/* Timeline column */}
      <div className="shrink-0 flex flex-col items-center relative">
        {/* Dot */}
        <div
          className={`relative z-10 rounded-full border-2 mt-1.5 ${
            isSubEntry
              ? "w-2.5 h-2.5 border-neutral-300 dark:border-neutral-600 bg-white dark:bg-[#0c0c0e]"
              : "w-3.5 h-3.5 border-neutral-900 dark:border-neutral-100 bg-neutral-900 dark:bg-neutral-100"
          }`}
        />
        {/* Vertical line */}
        {!isLast && (
          <div className="flex-1 w-px bg-neutral-200 dark:bg-neutral-800" />
        )}
      </div>

      {/* Content */}
      <div className={`flex-1 min-w-0 ${isLast ? "pb-0" : "pb-6"}`}>
        {/* Date */}
        <div className={`${isSubEntry ? "text-[11px]" : "text-xs"} text-neutral-400 dark:text-neutral-500 tabular-nums mb-1`}>
          {[
            entry.startMonth || entry.startYear
              ? `${entry.startMonth || ""} ${entry.startYear || ""}`.trim()
              : null,
            entry.endMonth || entry.endYear
              ? `${entry.endMonth || ""} ${entry.endYear || ""}`.trim()
              : null,
          ]
            .filter(Boolean)
            .join(" \u2192 ")}
        </div>

        {/* Title */}
        <h3
          className={`font-bold leading-snug ${
            isSubEntry
              ? "text-sm text-neutral-700 dark:text-neutral-300"
              : "text-base text-neutral-900 dark:text-neutral-100"
          }`}
        >
          {entry.name}
        </h3>
        <p
          className={`${
            isSubEntry ? "text-xs" : "text-sm"
          } text-neutral-400 dark:text-neutral-500 mt-0.5`}
        >
          {entry.title}
        </p>

        {/* Skills tags */}
        {skills.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {skills.map((skill) => {
              const level = getSkillLevel(skill, allSkillItems);
              const baseClass =
                level === "REGULAR"
                  ? "exp-tag-regular"
                  : level === "SOMETIMES"
                  ? "exp-tag-outline"
                  : "exp-tag-old";
              const highlightClass =
                profile !== "all" && isSkillRelevant(skill)
                  ? "!bg-neutral-900 !text-white dark:!bg-white dark:!text-neutral-900 !border-transparent"
                  : "";
              const mutedClass =
                profile !== "all" && !isSkillRelevant(skill) ? "opacity-30" : "";
              return (
                <span
                  key={skill}
                  className={`${baseClass} ${highlightClass} ${mutedClass}`}
                >
                  {skill}
                </span>
              );
            })}
          </div>
        )}

        {/* Context always visible */}
        {entry.context && (
          <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400 italic leading-relaxed">
            {parse(entry.context)}
          </p>
        )}

        {/* Collapsible descriptions */}
        {hasDescriptions && entry.descriptions && entry.descriptions.length > 0 && (
          <>
            <button
              onClick={() => setDetailsOpen(!detailsOpen)}
              className="no-print mt-2 text-[11px] font-medium text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors flex items-center gap-1"
            >
              {detailsOpen ? "Masquer" : "Détails"}
              <i
                className={`fa fa-chevron-down text-[8px] transition-transform duration-300 ${
                  detailsOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            <div
              className={`exp-collapsible overflow-hidden transition-all duration-500 ${
                detailsOpen ? "max-h-[3000px] opacity-100 mt-2" : "max-h-0 opacity-0"
              }`}
            >
              <div className="space-y-2 pl-3 border-l-2 border-neutral-100 dark:border-neutral-800">
                {entry.descriptions.map((desc, i) =>
                  desc.startsWith("@isHTML") ? (
                    <div
                      key={i}
                      className="text-sm text-neutral-600 dark:text-neutral-300 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_li]:text-sm"
                    >
                      {parse(desc.replace("@isHTML", ""))}
                    </div>
                  ) : (
                    <p
                      key={i}
                      className="text-sm text-neutral-600 dark:text-neutral-300 leading-relaxed"
                    >
                      {desc}
                    </p>
                  )
                )}
              </div>
            </div>
          </>
        )}

        {/* Sub-entries — always visible */}
        {hasSubs && (
          <div className="mt-4">
            {entry.subreferences!.map((sub, i) => (
              <ExperienceEntry
                key={i}
                entry={sub}
                allSkillItems={allSkillItems}
                isSubEntry
                isLast={i === entry.subreferences!.length - 1}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
